/* =============================================================================
   VIEWS / DELIVERY-FORM — Bestell-Modal "Lieferung" (S61 Phase D)
   =============================================================================
   Eigenständiges Pop-Up für den Liefer-Bestellflow. Wird vom Service-Type-
   Toggle im Reservierungsformular per Klick auf "Lieferung" geöffnet.
   Das Reservierungsformular bleibt unangetastet — wir legen das Modal nur
   als Overlay darüber.

   Inhalt (Schritt-für-Schritt):
     1. Speisekarte mit Warenkorb (eingebetteter menu-picker)
     2. Lieferadresse: Straße + Hausnr., PLZ, Stadt, Etage, Notiz für Fahrer
        — PLZ-Live-Check via RPC `check_delivery_zone` (Debounce 400ms)
        — bei Treffer: Liefergebühr + Mindestbestellwert anzeigen
        — bei Mindestbestellwert-Unterschreitung: Submit-Button disabled
     3. Wunschzeit (optional, sonst „so schnell wie möglich")
     4. Kontaktdaten (Name, Telefon, E-Mail)

   Submit: orders.createCustomer mit serviceType='delivery' + deliveryAddress.
   Die RPC create_customer_order schreibt die Order mit service_type='delivery'
   und der delivery_address JSONB. Cockpit-KDS/Theke sieht die Order danach
   ganz normal in der Pipeline (Single-Pipeline-Garantie aus S61).

   Foundation aus S59 (delivery_zones, check_delivery_zone-RPC) + S60
   (admin-Liefer-Pfad in admin-order-entry.js) wird hier auf die Customer-
   Seite gespiegelt.
============================================================================ */

import { htmlEscape, fmtEUR, todayISO, fmtDate, showToast } from '../utils/helpers.js';
import { generateTimeSlots } from '../core/reservation-engine.js';
import {
  initMenuPicker,
  getOrder as getMenuOrder,
  validateOrder as validateMenuOrder
} from './menu-picker.js';
// S234 T3 — geteilter Stripe-Checkout-Flow (Single-Source Takeaway + Lieferung).
import { injectStripeCheckoutBlock } from '../core/stripe-checkout.js';
// S256 — dieselbe Übersetzung wie Abholung und Tisch: nur Kennungen und Mengen.
import { lineToOrderRpcItem, friendlyOrderError } from './menu-picker-helpers.js';
// S266 — Gast-Kanäle: Server-Ablehnung erkennen (CHANNEL_DISABLED:delivery).
import { channelDisabledKeyFromError } from '../core/guest-channels.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

function currentRestaurantIdLazy() {
  if (window.App && typeof window.App.getCurrentRestaurantId === 'function') {
    return window.App.getCurrentRestaurantId();
  }
  return (window.AppConfig && window.AppConfig.defaultRestaurantId) || 'rest_default';
}

let _escBound = false;
function bindEscOnce() {
  if (_escBound || typeof document === 'undefined') return;
  _escBound = true;
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.getElementById('delivery-modal-overlay')) {
      e.preventDefault();
      closeDeliveryModal();
    }
  });
}

// State für den PLZ-Check + Cart-Total
let _plzCheckTimer = null;
let _currentZone = null;  // { zone_id, name, fee_cents, min_order_cents } oder null
let _cartTotalCents = 0;  // wird aus menu-picker abgeleitet

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

export function openDeliveryModal() {
  if (document.getElementById('delivery-modal-overlay')) return;
  bindEscOnce();

  const state = getState();
  const brand = (state.settings && state.settings.restaurantName) || 'Grill & Green';

  // Default-Pickup-Zeit: heute + 60 Min (Liefer-Lead-Zeit), gerundet auf nächste halbe Stunde
  const now = new Date();
  const leadMin = Number((state.settings && state.settings.deliveryLeadTimeMin) || 60);
  const target = new Date(now.getTime() + leadMin * 60000);
  // auf nächste halbe Stunde aufrunden
  const targetMin = target.getMinutes();
  const roundedMin = targetMin <= 30 ? 30 : 60;
  target.setMinutes(roundedMin === 60 ? 0 : 30);
  if (roundedMin === 60) target.setHours(target.getHours() + 1);
  const defaultTime = `${String(target.getHours()).padStart(2,'0')}:${String(target.getMinutes()).padStart(2,'0')}`;

  const overlayHTML = `
    <div id="delivery-modal-overlay" class="ta-modal-overlay" onclick="if(event.target===this){closeDeliveryModal();}">
      <div class="ta-modal" role="dialog" aria-modal="true" aria-labelledby="dv-modal-title">
        <div class="ta-modal-header">
          <div class="ta-modal-head-text">
            <div id="dv-modal-title" class="ta-modal-title">🛵 Lieferung bestellen</div>
            <div class="ta-modal-subtitle">
              Bestellung vom <strong>${htmlEscape(brand)}</strong> direkt zu dir nach Hause.
            </div>
          </div>
          <button type="button" class="ta-modal-close"
                  onclick="closeDeliveryModal()" aria-label="Schließen">✕</button>
        </div>

        <div class="ta-modal-body">
          <!-- 1. Speisekarte -->
          <section class="ta-section">
            <h3 class="ta-section-head">1. Wähle deine Gerichte</h3>
            <div id="delivery-menu-root"></div>
          </section>

          <!-- 2. Lieferadresse -->
          <section class="ta-section">
            <h3 class="ta-section-head">2. Lieferadresse</h3>
            <div class="field-row">
              <div class="field" style="flex:2;">
                <label for="dv-street">Straße + Hausnummer *</label>
                <input id="dv-street" type="text" required autocomplete="street-address"
                       placeholder="z.B. Musterstraße 12">
              </div>
              <div class="field" style="flex:1;">
                <label for="dv-plz">PLZ *</label>
                <input id="dv-plz" type="text" required inputmode="numeric" pattern="\\d{4,5}" maxlength="5"
                       autocomplete="postal-code" placeholder="20095">
              </div>
            </div>
            <div class="field-row">
              <div class="field" style="flex:2;">
                <label for="dv-city">Stadt *</label>
                <input id="dv-city" type="text" required autocomplete="address-level2"
                       placeholder="Hamburg">
              </div>
              <div class="field" style="flex:1;">
                <label for="dv-floor">Etage / Aufgang</label>
                <input id="dv-floor" type="text" placeholder="z.B. 3. OG">
              </div>
            </div>
            <div class="field">
              <label for="dv-driver-notes">Notiz für den Fahrer (optional)</label>
              <input id="dv-driver-notes" type="text"
                     placeholder="z.B. Klingelschild „Mustermann", bei Regen vor die Tür">
            </div>

            <!-- PLZ-Check-Result-Block (dynamisch befüllt) -->
            <div id="dv-zone-result" style="margin-top:10px; padding:12px 14px; border-radius:8px; font-size:0.92rem; display:none;"></div>
          </section>

          <!-- 3. Wunschzeit -->
          <section class="ta-section">
            <h3 class="ta-section-head">3. Wunsch-Lieferzeit</h3>
            <div class="field-row">
              <div class="field">
                <label for="dv-date">Datum *</label>
                <input id="dv-date" type="date" required>
              </div>
              <div class="field">
                <label for="dv-time">Uhrzeit *</label>
                <input id="dv-time" type="time" required value="${defaultTime}">
                <div class="hint" style="margin-top:6px;color:var(--ink-muted,#6b7280);font-size:12px;">
                  Mindest-Vorlauf: ${leadMin} Min
                </div>
              </div>
            </div>
          </section>

          <!-- 4. Kontakt -->
          <section class="ta-section">
            <h3 class="ta-section-head">4. Deine Kontaktdaten</h3>
            <div class="field">
              <label for="dv-name">Vollständiger Name *</label>
              <input id="dv-name" type="text" required autocomplete="name">
            </div>
            <div class="field-row">
              <div class="field">
                <label for="dv-phone">Telefonnummer *</label>
                <input id="dv-phone" type="tel" required autocomplete="tel">
              </div>
              <div class="field">
                <label for="dv-email">E-Mail-Adresse *</label>
                <input id="dv-email" type="email" required autocomplete="email">
              </div>
            </div>
            <div class="field" id="dv-gdpr-field" style="display:none;">
              <label class="checkbox">
                <input type="checkbox" id="dv-gdpr">
                <span>Ich willige ein, dass meine Daten zur Bearbeitung der Bestellung verarbeitet werden. <span id="dv-gdpr-link"></span></span>
              </label>
            </div>
            <!-- S226 — Newsletter-Abo (Double-Opt-in), UNVORAUSGEWÄHLT. -->
            <div class="field" id="dv-newsletter-field">
              <label class="checkbox">
                <input type="checkbox" id="dv-newsletter">
                <span>Ja, ich möchte den Newsletter abonnieren. Ich erhalte eine Bestätigungsmail und kann mich jederzeit kostenlos abmelden.</span>
              </label>
            </div>
          </section>
        </div>

        <div class="ta-modal-footer">
          <div class="ta-modal-actions">
            <button type="button" class="btn btn-ghost"
                    onclick="closeDeliveryModal()">Abbrechen</button>
            <button type="button" class="btn btn-primary"
                    id="dv-submit-btn"
                    onclick="submitDeliveryOrder()">Bestellung absenden</button>
          </div>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', overlayHTML);
  document.body.style.overflow = 'hidden';

  // Default-Datum auf heute
  const d = document.getElementById('dv-date');
  if (d) { d.min = todayISO(); d.value = todayISO(); }

  // GDPR-Block je nach Settings
  if (state.settings && state.settings.requireGdprConsent) {
    const g = document.getElementById('dv-gdpr-field');
    if (g) g.style.display = 'block';
    const link = document.getElementById('dv-gdpr-link');
    if (link && state.settings.gdprPolicyUrl) {
      link.innerHTML = `<a href="${htmlEscape(state.settings.gdprPolicyUrl)}" target="_blank" rel="noopener">Mehr erfahren</a>`;
    }
  }

  // PLZ-Live-Check binden (Debounce 400ms)
  const plzInput = document.getElementById('dv-plz');
  if (plzInput) {
    plzInput.addEventListener('input', () => {
      clearTimeout(_plzCheckTimer);
      const plz = (plzInput.value || '').trim();
      // Result-Block vorerst ausblenden
      const result = document.getElementById('dv-zone-result');
      if (result) result.style.display = 'none';
      _currentZone = null;
      _refreshSubmitState();
      if (plz.length < 4) return; // zu kurz für sinnvolle Prüfung
      _plzCheckTimer = setTimeout(() => _checkDeliveryZone(plz), 400);
    });
  }

  // menu-picker mit eigenem Root mounten + Cart-Listener für Mindestbestellwert
  initMenuPicker('delivery-menu-root');
  // alle 800ms den Cart-Total beobachten (einfacher Polling-Ansatz, robust gegen
  // menu-picker-interne Updates ohne explizites Event)
  const cartWatcher = setInterval(() => {
    if (!document.getElementById('delivery-modal-overlay')) {
      clearInterval(cartWatcher);
      return;
    }
    try {
      const order = getMenuOrder();
      const sub = (order && Array.isArray(order.items))
        ? order.items.reduce((s, it) => s + (Number(it.lineTotal) * 100 || 0), 0)
        : 0;
      _cartTotalCents = Math.round(sub);
      _refreshSubmitState();
    } catch (_) { /* defensive */ }
  }, 800);
}

/** Schließt das Delivery-Modal. opts.silent=true unterdrückt das Toggle-Reset. */
export function closeDeliveryModal(opts) {
  const overlay = document.getElementById('delivery-modal-overlay');
  if (overlay) overlay.remove();
  document.body.style.overflow = '';
  _currentZone = null;
  _cartTotalCents = 0;

  if (typeof window.initMenuPicker === 'function') {
    try { window.initMenuPicker('menu-picker-root'); } catch (_) {}
  }

  if (!opts || !opts.silent) {
    if (typeof window._resetServiceTypeToggleSilently === 'function') {
      window._resetServiceTypeToggleSilently();
    }
  }
}

/** Fragt die Delivery-Zone für eine PLZ ab (RPC check_delivery_zone)
 *  und rendert das Resultat im #dv-zone-result-Block. */
async function _checkDeliveryZone(plz) {
  const result = document.getElementById('dv-zone-result');
  if (!result) return;
  result.style.display = 'block';
  result.style.background = '#f5f5f5';
  result.style.color = '#444';
  result.style.border = '1px solid #ddd';
  result.innerHTML = '⏳ Prüfe Liefergebiet …';

  try {
    if (!window.App || !window.App.storage || !window.App.storage.deliveryZones
        || typeof window.App.storage.deliveryZones.checkPlz !== 'function') {
      throw new Error('deliveryZones-Adapter nicht verfügbar');
    }
    const zone = await window.App.storage.deliveryZones.checkPlz(plz);
    if (!zone || !zone.zone_id) {
      _currentZone = null;
      result.style.background = '#fff5e8';
      result.style.color = '#a35a1c';
      result.style.border = '1px solid #d97a2c';
      result.innerHTML = `❌ <strong>Außerhalb des Liefergebiets</strong> · PLZ ${htmlEscape(plz)} ist aktuell nicht abgedeckt.`;
      _refreshSubmitState();
      return;
    }
    _currentZone = zone;
    const fee = (Number(zone.fee_cents) / 100).toFixed(2).replace('.', ',');
    const min = (Number(zone.min_order_cents) / 100).toFixed(2).replace('.', ',');
    result.style.background = '#e9f5ec';
    result.style.color = '#2f6b3a';
    result.style.border = '1px solid #2f6b3a';
    result.innerHTML =
      `✓ <strong>Wir liefern zu dir!</strong> Zone: ${htmlEscape(zone.name || '–')}<br>` +
      `<span style="font-size:0.88rem;">Liefergebühr: <strong>${fee} €</strong> · Mindestbestellwert: <strong>${min} €</strong></span>`;
    _refreshSubmitState();
  } catch (err) {
    console.error('[deliveryForm] checkPlz:', err);
    _currentZone = null;
    result.style.background = '#fce8e8';
    result.style.color = '#a02525';
    result.style.border = '1px solid #c93333';
    result.innerHTML = '⚠️ Liefergebiet-Prüfung fehlgeschlagen. Bitte später erneut versuchen oder uns telefonisch kontaktieren.';
    _refreshSubmitState();
  }
}

/** Aktualisiert den Submit-Button-Zustand basierend auf
 *  Liefergebiet-Treffer + Mindestbestellwert. */
function _refreshSubmitState() {
  const btn = document.getElementById('dv-submit-btn');
  if (!btn) return;
  const result = document.getElementById('dv-zone-result');
  if (!_currentZone) {
    btn.disabled = false; // Vor PLZ-Check Submit nicht hart blockieren
    btn.textContent = 'Bestellung absenden';
    return;
  }
  const min = Number(_currentZone.min_order_cents) || 0;
  if (_cartTotalCents < min) {
    btn.disabled = true;
    const diff = ((min - _cartTotalCents) / 100).toFixed(2).replace('.', ',');
    btn.textContent = `Noch ${diff} € fehlen für Lieferung`;
    return;
  }
  btn.disabled = false;
  btn.textContent = 'Bestellung absenden';
}

/** Liefert den MwSt-Satz für ein menu_item — Speisen 7%, Getränke 19%. */
function deriveTaxRate(menuItem, settings) {
  const food   = Number((settings && settings.taxRateFood)   || 7);
  const drinks = Number((settings && settings.taxRateDrinks) || 19);
  const itemType = (menuItem && menuItem.itemType) || 'main';
  return itemType === 'drink' ? drinks : food;
}


/** Submit-Handler — validiert + ruft orders.createCustomer mit
 *  serviceType='delivery' + deliveryAddress. */
export async function submitDeliveryOrder() {
  const state = getState();

  const name   = (document.getElementById('dv-name')?.value || '').trim();
  const phone  = (document.getElementById('dv-phone')?.value || '').trim();
  const email  = (document.getElementById('dv-email')?.value || '').trim();
  const date   =  document.getElementById('dv-date')?.value || '';
  const time   =  document.getElementById('dv-time')?.value || '';
  const street = (document.getElementById('dv-street')?.value || '').trim();
  const plz    = (document.getElementById('dv-plz')?.value || '').trim();
  const city   = (document.getElementById('dv-city')?.value || '').trim();
  const floor  = (document.getElementById('dv-floor')?.value || '').trim();
  const notes  = (document.getElementById('dv-driver-notes')?.value || '').trim();

  if (!name)   return showToast('Bitte deinen Namen eingeben.', 'error');
  if (!phone)  return showToast('Bitte deine Telefonnummer eingeben.', 'error');
  if (!email)  return showToast('Bitte deine E-Mail-Adresse eingeben.', 'error');
  if (!street) return showToast('Bitte Straße + Hausnummer eingeben.', 'error');
  if (!plz)    return showToast('Bitte deine PLZ eingeben.', 'error');
  if (!city)   return showToast('Bitte deine Stadt eingeben.', 'error');
  if (!date)   return showToast('Bitte ein Lieferdatum wählen.', 'error');
  if (!time)   return showToast('Bitte eine Lieferzeit wählen.', 'error');
  if (date < todayISO()) return showToast('Das Lieferdatum darf nicht in der Vergangenheit liegen.', 'error');

  if (!_currentZone || !_currentZone.zone_id) {
    return showToast('PLZ ist nicht im Liefergebiet — bitte andere Adresse versuchen.', 'error');
  }
  const minCents = Number(_currentZone.min_order_cents) || 0;
  if (_cartTotalCents < minCents) {
    const diff = ((minCents - _cartTotalCents) / 100).toFixed(2).replace('.', ',');
    return showToast(`Mindestbestellwert nicht erreicht — noch ${diff} € fehlen.`, 'error');
  }

  if (state.settings && state.settings.requireGdprConsent) {
    const consent = document.getElementById('dv-gdpr');
    if (!consent || !consent.checked) {
      return showToast('Bitte stimme den Datenschutzbestimmungen zu.', 'error');
    }
  }

  // Warenkorb prüfen
  const vr = validateMenuOrder();
  if (!vr.ok) {
    return showToast(vr.errors[0] || 'Bitte vervollständige deine Bestellung.', 'error');
  }
  const order = getMenuOrder();
  // S169 P4 — Deal-Positionen → echte order_items (Decomposition).
  const dealItems = (typeof window.getDealOrderItems === 'function') ? window.getDealOrderItems() : [];
  if (!order || !Array.isArray(order.items) || (order.items.length === 0 && dealItems.length === 0)) {
    return showToast('Bitte mindestens ein Gericht auswählen.', 'error');
  }

  // S256 — Deal-Zeilen werden nicht mehr im Client zerlegt (siehe takeaway-form).
  const menuList = Array.isArray(state.menu) ? state.menu : [];
  const items = order.items.map(line => lineToOrderRpcItem(line, menuList, state.settings)).filter(Boolean);

  const req = {
    restaurantId:     currentRestaurantIdLazy(),
    name, phone, email,
    pickupDate:       date,
    pickupTime:       time,
    items,
    preorderSnapshot: order,
    notes:            null,
    specialRequests:  order.specialRequests || null,
    gdprConsentAt:    (state.settings && state.settings.requireGdprConsent) ? new Date().toISOString() : null,
    // S61 Phase D — Delivery-spezifische Felder
    serviceType:      'delivery',
    deliveryAddress: {
      street, plz, city,
      floor: floor || null,
      driver_notes: notes || null,
      zone_id: _currentZone.zone_id || null,
      // S61 — zone_name für KDS/Theke-Badge ("🛵 Lieferung · Innenstadt")
      zone_name: _currentZone.name || null,
      fee_cents: Number(_currentZone.fee_cents) || 0,
      min_order_cents: Number(_currentZone.min_order_cents) || 0
    }
  };

  let created;
  try {
    created = await window.App.storage.orders.createCustomer(req);
  } catch (err) {
    console.error('[submitDeliveryOrder] orders.createCustomer:',
      (err && (err.code || '')) || '', err);
    showToast(friendlyOrderError(err), 'error');
    // S266 — Kanal „Lieferung" wurde geschlossen, während das Modal offen war.
    if (channelDisabledKeyFromError(err) && typeof window.refreshGuestChannels === 'function') {
      window.refreshGuestChannels().catch(() => {});
    }
    return;
  }

  // S226 — Newsletter-Abo (Double-Opt-in) NICHT-FATAL: Häkchen lesen, BEVOR
  // closeDeliveryModal() das Modal-DOM entfernt. Quelle web_delivery.
  if (typeof window.maybeSubscribeFromCheckbox === 'function') {
    window.maybeSubscribeFromCheckbox('dv-newsletter', req.restaurantId, req.email, 'web_delivery');
  }

  if (window.App.events) {
    try { await window.App.events.emit('order:created', created); } catch (_e) {}
  }

  closeDeliveryModal();

  const successWrap   = document.getElementById('customer-success');
  const successMsgEl  = document.getElementById('success-message');
  const formWrapper   = document.getElementById('customer-form-wrapper');
  if (successMsgEl) {
    const num = created && created.orderNumber ? ` (Nr. ${created.orderNumber})` : '';
    // S74 Konzept-Cut: kein „gleich eine Bestätigungs-Mail" mehr — die kommt
    // erst nach Operator-Annahme. Confirmation-Screen + Hinweis darunter liefern
    // die visuelle Eingangsbestätigung; Mail folgt mit confirmed.
    // S75 Polish: Datum deutsch lokalisiert (fmtDate → „Fr., 29. Mai 2026")
    // statt rohem ISO `2026-05-29` — analog zur v27-Edge-Function-Mail.
    successMsgEl.textContent =
      `Danke für deine Bestellung${num}! 🛵 Wir geben unser Bestes, ` +
      `deine Lieferung pünktlich am ${fmtDate(date)} gegen ${time} Uhr an dich zu übergeben.`;
  }
  if (formWrapper) formWrapper.style.display = 'none';
  if (successWrap) successWrap.style.display = 'block';

  // S74 Konzept-Cut: Cancel-Link + Hinweis prominent (Mail-Backup-Ersatz).
  try {
    const hint     = document.getElementById('success-confirmation-hint');
    const linkWrap = document.getElementById('success-cancel-link-wrap');
    const linkEl   = document.getElementById('success-cancel-link');
    if (hint) hint.style.display = '';
    const tok = created?.cancelToken || created?.cancel_token;
    if (linkWrap && linkEl && tok) {
      const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
      const href = `${origin}/index.html#cancel=${tok}`;
      linkEl.href = href;
      linkEl.textContent = href;
      linkWrap.style.display = '';
    }
  } catch (e) {
    console.warn('[delivery-form] Confirmation-Screen-Cancel-Link konnte nicht gefüllt werden:', e);
  }

  // S234 T3 — Online-Bezahlung (Vorkasse) auch für Lieferung: geteilte Stripe-
  // Insel, Kanal 'delivery' (togglebar via settings.stripeChannels). Optional,
  // kein Gate — der Gast kann auch bei Lieferung online begleichen.
  injectStripeCheckoutBlock({
    successBox: document.querySelector('#customer-success .success-box'),
    order: created,
    channel: 'delivery'
  });
}

// Backward-Compat — window-exposed für CSP-Inline-Handler-Pattern
// (Memory `feedback_gg_inline_handler_csp`: KEIN `window.func()`-Präfix in
// onclick-Strings, der dom-bindings.js-Parser löst gegen window auf).
if (typeof window !== 'undefined') {
  Object.assign(window, {
    openDeliveryModal,
    closeDeliveryModal,
    submitDeliveryOrder
  });
}

// Test-Hook-Exports
export {
  deriveTaxRate as _deriveTaxRate
};
